'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Input,
  Portal,
  Spinner,
  Textarea,
} from '@chakra-ui/react'
import { EDonorLabAdmissionDecision } from '@entities/donor/model'
import { AppField } from '@/shared'
import { useLabAdmissionDecision } from '../model/use-lab-admission-decision'

interface Props {
  donorId: string
  /** False unless the donor card sits at UNDER_THERAPIST_REVIEW. */
  canSetAdmission: boolean
}

/**
 * The two donor-workflow verdicts taken off the lab results: admit to donation
 * straight away, or defer with a reason and an end date.
 */
export function LabAdmissionDecisionActions({
  donorId,
  canSetAdmission,
}: Props) {
  const { t } = useTranslation()
  const [deferralOpen, setDeferralOpen] = useState(false)

  const { form, pendingDecision, admitToDonation, submitDeferral } =
    useLabAdmissionDecision({
      donorId,
      onDeferralSuccess: () => setDeferralOpen(false),
    })

  const {
    register,
    reset,
    formState: { errors },
  } = form

  const isPending = pendingDecision !== null
  const disabled = !canSetAdmission || !donorId || isPending

  const closeDeferral = () => {
    reset()
    setDeferralOpen(false)
  }

  return (
    <>
      <Flex justify="flex-end" gap={3} wrap="wrap">
        <Button
          bg="primary.solid"
          color="white"
          borderRadius="md"
          px={6}
          disabled={disabled}
          onClick={admitToDonation}
          _hover={{ opacity: 0.85 }}
          _active={{ opacity: 0.7 }}
        >
          {pendingDecision === EDonorLabAdmissionDecision.TO_DONATION ? (
            <Spinner size="xs" color="white" />
          ) : (
            t('donor_detail.lab_research.to_donation')
          )}
        </Button>
        <Button
          variant="outline"
          borderRadius="md"
          px={6}
          color="primary.fg"
          borderColor="primary.fg"
          disabled={disabled}
          onClick={() => setDeferralOpen(true)}
          _hover={{ bg: 'primary.solid', color: 'white' }}
        >
          {t('donor_detail.lab_research.medical_deferral')}
        </Button>
      </Flex>

      <Dialog.Root
        open={deferralOpen}
        onOpenChange={({ open }) => {
          if (!open) closeDeferral()
        }}
        size="md"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="bg.surface" maxW="520px">
              <Dialog.Header pb={4}>
                <Dialog.Title fontSize="lg" fontWeight="bold">
                  {t('donor_detail.lab_research.decision.deferral_title')}
                </Dialog.Title>
                <Dialog.CloseTrigger
                  asChild
                  position="absolute"
                  top={4}
                  right={4}
                >
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>

              <Dialog.Body py={2}>
                <form
                  id="lab-admission-deferral-form"
                  onSubmit={submitDeferral}
                >
                  <Flex direction="column" gap={4}>
                    <AppField.Root invalid={!!errors.reason}>
                      <AppField.Label fontSize="sm">
                        {t('donor_detail.lab_research.decision.reason_label')}
                      </AppField.Label>
                      <Textarea
                        {...register('reason')}
                        rows={3}
                        size="sm"
                        bg="bg.subtle"
                        borderColor={errors.reason ? undefined : 'border'}
                        borderRadius="md"
                      />
                      <AppField.ErrorText>
                        {errors.reason?.message}
                      </AppField.ErrorText>
                    </AppField.Root>

                    <AppField.Root invalid={!!errors.deferral_end_date}>
                      <AppField.Label fontSize="sm">
                        {t('donor_detail.lab_research.decision.date_label')}
                      </AppField.Label>
                      <Input
                        {...register('deferral_end_date')}
                        type="date"
                        size="sm"
                        bg="bg.subtle"
                        borderColor={
                          errors.deferral_end_date ? undefined : 'border'
                        }
                        borderRadius="md"
                      />
                      <AppField.ErrorText>
                        {errors.deferral_end_date?.message}
                      </AppField.ErrorText>
                    </AppField.Root>
                  </Flex>
                </form>
              </Dialog.Body>

              <Dialog.Footer pt={4} justifyContent="flex-end">
                <Button
                  variant="outline"
                  borderRadius="md"
                  size="sm"
                  px={6}
                  onClick={closeDeferral}
                >
                  {t('donor_detail.lab_research.decision.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="lab-admission-deferral-form"
                  bg="primary.solid"
                  color="white"
                  borderRadius="md"
                  size="sm"
                  px={6}
                  disabled={isPending}
                  _hover={{ opacity: 0.85 }}
                  _active={{ opacity: 0.7 }}
                >
                  {pendingDecision ===
                  EDonorLabAdmissionDecision.MEDICAL_DEFERRAL ? (
                    <Spinner size="xs" color="white" />
                  ) : (
                    t('donor_detail.lab_research.decision.submit')
                  )}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
